import React, { FC, useState, useEffect } from 'react'
import clsx from 'clsx'
import { FormattedMessage } from 'react-intl'
import { makeStyles, useTheme } from '@material-ui/core/styles'
import {
  ButtonBase,
  Theme,
  Typography,
  Collapse,
  Container,
  useMediaQuery,
} from '@material-ui/core'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import { ProductFaqsQuery } from '@src/queries/ProductFaqsQuery.generated'
import messages from '@utils/messages'
import { getVerticalRhythm } from '@src/styles/utils/vrStyles'
import ProductFAQItem from './ProductFAQItem'
import SectionAccordion, {
  useSectionAccordion,
} from '@src/components/SectionAccordion'
import { sendProductDetailsEvent } from '@src/utils/analytics'
import { ProductDetailsEventAction as eventAction } from '@utils/analytics/enums'

const ACCORDION_KEY = 'PRODUCT_FAQS'

export const useStyles = makeStyles((theme: Theme) => {
  return {
    faqList: {
      listStyleType: 'none',
      margin: 0, // Remove all default styles from the OL, needed for ie11
      padding: 0, // Needed for ie11
      marginBlockStart: 0,
      paddingInlineStart: 0,
      ...getVerticalRhythm(1), // Add specific margin bottom for spacing
    },
    content: {
      padding: theme.spacing(4, 4, 8),
      [theme.breakpoints.up('sm')]: {
        padding: theme.spacing(10),
      },
    },
    showMore: {
      fontSize: theme.typography.pxToRem(16),
      fontWeight: theme.typography.fontWeightBold,
      color: theme.palette.primary.main,
      display: 'flex',
      alignItems: 'center',
    },
    expand: {
      fontSize: theme.typography.pxToRem(18),
      padding: theme.spacing(0),
      transform: 'rotate(0deg)',
      transition: theme.transitions.create('transform', {
        duration: theme.transitions.duration.shortest,
      }),
    },
    expandOpen: {
      transform: 'rotate(180deg)',
    },
  }
})

interface ProductFaqProps {
  faqs: ProductFaqsQuery['getProductFaqs']
  productNumber?: string
}

const ProductFAQ: FC<ProductFaqProps> = ({ faqs }) => {
  const [expanded, setExpanded] = useState(false)
  const classes = useStyles()
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))
  const isPrint = useMediaQuery('print')
  const isAccordionEnabled = !isDesktop && !isPrint
  const limit = isDesktop ? 8 : faqs.length
  const hasExpanded = faqs.length > limit
  const { accordionProps, toggleIsAccordionOpen } =
    useSectionAccordion(ACCORDION_KEY)

  useEffect(() => {
    if (expanded) {
      sendProductDetailsEvent(eventAction.FaqShowMore)
    }
  }, [expanded])

  if (!faqs.length) return null
  return (
    <div>
      <SectionAccordion
        testId="pdp-faqs"
        {...accordionProps}
        isAccordionEnabled={isAccordionEnabled}
        toggleAccordion={toggleIsAccordionOpen}
        renderHeading={() => (
          <Typography variant="h2" component="h2">
            <FormattedMessage {...messages.FREQUENTLY_ASKED_QUESTIONS} />
          </Typography>
        )}
      >
        <Container maxWidth="lg">
          <ol className={classes.faqList}>
            {faqs.slice(0, limit).map((faq, index) => (
              <li key={index}>
                <ProductFAQItem faq={faq}></ProductFAQItem>
              </li>
            ))}
            {hasExpanded ? (
              <>
                <Collapse in={expanded} timeout="auto">
                  {faqs.slice(limit, faqs.length).map((faq, index) => (
                    <li key={index}>
                      <ProductFAQItem faq={faq}></ProductFAQItem>
                    </li>
                  ))}
                </Collapse>
                <ButtonBase
                  onClick={() => setExpanded(!expanded)}
                  aria-expanded={expanded}
                  aria-label="Show more"
                  className={classes.showMore}
                >
                  <div>
                    {expanded ? (
                      <FormattedMessage {...messages.SHOW_LESS} />
                    ) : (
                      <>
                        <FormattedMessage {...messages.SHOW_MORE} />{' '}
                      </>
                    )}
                  </div>
                  <ExpandMoreIcon
                    className={clsx(classes.expand, {
                      [classes.expandOpen]: expanded,
                    })}
                  />
                </ButtonBase>
              </>
            ) : null}
          </ol>
        </Container>
      </SectionAccordion>
    </div>
  )
}

export default ProductFAQ
